import { useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import {
    RadioInput,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    ConditionStatus,
    ConditionType,
    IEditConsultationNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    SessionLocations,
    Modality,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { CONSULTATION_NOTE_MED_LABELS } from "../../../../utils/constants";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface EditConsultationPsychNoteProps {
    noteDetails: ISessionNote;
}

export default function EditConsultationMedNote({
    noteDetails,
}: EditConsultationPsychNoteProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditConsultationNoteMedPayload, { isDraft: boolean }>({
        resolver: yupResolver(
            yup.object({
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("Session location is required"),
                    otherwise: yup.string(),
                }),
                prolonged_care_different_day_as_em_visit: yup
                    .array()
                    .nullable(),
                details_of_prolonged_care_different_day_as_em_visit:
                    yup.string(),
                modality: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Modality is required"),
                    otherwise: yup.string(),
                }),
                total_amount_of_time: yup
                    .string()
                    .test(
                        "valid-positive-number",
                        "Enter a number greater than zero",
                        (value) =>
                            !value ||
                            (Number.isFinite(Number(value)) &&
                                Number(value) > 0)
                    ),
                condition_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Condition Type is required"),
                    otherwise: yup.string(),
                }),
                condition_type_additional_details: yup.string(),
                condition_status: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Condition Status is required"),
                    otherwise: yup.string(),
                }),
                condition_status_additional_details: yup.string(),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: {
            location_of_the_client:
                noteDetails.note_content.location_of_the_client,
            other_location_of_the_client:
                noteDetails.note_content.other_location_of_the_client,
            location_of_the_provider:
                noteDetails.note_content.location_of_the_provider,
            other_location_of_the_provider:
                noteDetails.note_content.other_location_of_the_provider,
            consent_was_received_for_the_teletherapy_session:
                noteDetails.note_content
                    .consent_was_received_for_the_teletherapy_session,
            the_session_was_conducted_via_video:
                noteDetails.note_content.the_session_was_conducted_via_video,
            session_location: noteDetails.note_content.session_location ?? "",
            prolonged_care_different_day_as_em_visit:
                noteDetails?.note_content
                    ?.prolonged_care_different_day_as_em_visit ?? [],
            details_of_prolonged_care_different_day_as_em_visit:
                noteDetails?.note_content
                    ?.details_of_prolonged_care_different_day_as_em_visit,
            modality: noteDetails?.note_content?.modality,
            total_amount_of_time:
                noteDetails?.note_content?.total_amount_of_time,
            condition_type: noteDetails?.note_content?.condition_type,
            condition_type_additional_details:
                noteDetails?.note_content?.condition_type_additional_details,
            condition_status: noteDetails?.note_content?.condition_status,
            condition_status_additional_details:
                noteDetails?.note_content?.condition_status_additional_details,
            signature: noteDetails?.note_content?.signature || "",
        },
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        setValue("session_location", note.note_content.session_location ?? "");
        setValue(
            "prolonged_care_different_day_as_em_visit",
            note.note_content.prolonged_care_different_day_as_em_visit
        );
        setValue(
            "details_of_prolonged_care_different_day_as_em_visit",
            note.note_content
                .details_of_prolonged_care_different_day_as_em_visit
        );
        setValue("modality", note.note_content.modality);
        setValue(
            "total_amount_of_time",
            note.note_content.total_amount_of_time
        );
        setValue("condition_type", note.note_content.condition_type);
        setValue(
            "condition_type_additional_details",
            note.note_content.condition_type_additional_details
        );
        setValue("condition_status", note.note_content.condition_status);
        setValue(
            "condition_status_additional_details",
            note.note_content.condition_status_additional_details
        );
        setShowPrompt(false);
    };

    const handleIsChecked = (checkboxValue: string, values?: string[]) => {
        return Boolean(
            values?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const modalityOptionsForSelect = Object.keys(Modality).map((modality) => ({
        label: removeEnumUnderscore(modality),
        value: Modality[modality as keyof typeof Modality],
    }));

    const conditionTypeOptionsForSelect = Object.keys(ConditionType).map(
        (conditionType) => ({
            label: removeEnumUnderscore(conditionType),
            value: ConditionType[conditionType as keyof typeof ConditionType],
        })
    );

    const conditionStatusOptionsForSelect = Object.keys(ConditionStatus).map(
        (conditionStatus) => ({
            label: removeEnumUnderscore(conditionStatus),
            value: ConditionStatus[
                conditionStatus as keyof typeof ConditionStatus
            ],
        })
    );

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="consultation med note"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    CONSULTATION_NOTE_MED_LABELS.session_location
                                }
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {
                                CONSULTATION_NOTE_MED_LABELS.location_of_the_client
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        CONSULTATION_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {
                                CONSULTATION_NOTE_MED_LABELS.location_of_the_provider
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        CONSULTATION_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    CONSULTATION_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    CONSULTATION_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div>
                    <h3 className="note-label note-label-opt">
                        {
                            CONSULTATION_NOTE_MED_LABELS.prolonged_care_different_day_as_em_visit
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Time spent preparing to see patient"
                            value="Time spent preparing to see patient"
                            defaultChecked={handleIsChecked(
                                "Time spent preparing to see patient",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Obtaining / performing history and exam"
                            value="Obtaining / performing history and exam"
                            defaultChecked={handleIsChecked(
                                "Obtaining / performing history and exam",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Counseling and care coordination"
                            value="Counseling and care coordination"
                            defaultChecked={handleIsChecked(
                                "Counseling and care coordination",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Ordering of medications / tests / procedures"
                            value="Ordering of medications / tests / procedures"
                            defaultChecked={handleIsChecked(
                                "Ordering of medications / tests / procedures",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Communicating with other health care professionals"
                            value="Communicating with other health care professionals"
                            defaultChecked={handleIsChecked(
                                "Communicating with other health care professionals",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Documenting clinical information"
                            value="Documenting clinical information"
                            defaultChecked={handleIsChecked(
                                "Documenting clinical information",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Review and interpretation of tests"
                            value="Review and interpretation of tests"
                            defaultChecked={handleIsChecked(
                                "Review and interpretation of tests",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Communication to patient / family / caregiver"
                            value="Communication to patient / family / caregiver"
                            defaultChecked={handleIsChecked(
                                "Communication to patient / family / caregiver",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                        <FormCheckbox
                            {...register(
                                "prolonged_care_different_day_as_em_visit"
                            )}
                            label="Other"
                            value="Other"
                            defaultChecked={handleIsChecked(
                                "Other",
                                noteDetails?.note_content
                                    ?.prolonged_care_different_day_as_em_visit
                            )}
                        />
                    </div>
                    {errors.prolonged_care_different_day_as_em_visit ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "details_of_prolonged_care_different_day_as_em_visit"
                        )}
                        label={
                            CONSULTATION_NOTE_MED_LABELS.details_of_prolonged_care_different_day_as_em_visit
                        }
                        errorText={
                            errors
                                .details_of_prolonged_care_different_day_as_em_visit
                                ?.message
                        }
                        hasError={
                            !!errors.details_of_prolonged_care_different_day_as_em_visit
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="modality"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={CONSULTATION_NOTE_MED_LABELS.modality}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    modalityOptionsForSelect.find(
                                        (modality) =>
                                            modality.value === field.value
                                    ) || null
                                }
                                options={modalityOptionsForSelect}
                                hasError={!!errors.modality}
                                errorText={errors.modality?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("total_amount_of_time")}
                        label={
                            CONSULTATION_NOTE_MED_LABELS.total_amount_of_time
                        }
                        errorText={errors.total_amount_of_time?.message}
                        hasError={!!errors.total_amount_of_time}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="condition_type"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    CONSULTATION_NOTE_MED_LABELS.condition_type
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionTypeOptionsForSelect.find(
                                        (type) => type.value === field.value
                                    ) || null
                                }
                                options={conditionTypeOptionsForSelect}
                                hasError={!!errors.condition_type}
                                errorText={errors.condition_type?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_type_additional_details")}
                        label={
                            CONSULTATION_NOTE_MED_LABELS.condition_type_additional_details
                        }
                        errorText={
                            errors.condition_type_additional_details?.message
                        }
                        hasError={!!errors.condition_type_additional_details}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="condition_status"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    CONSULTATION_NOTE_MED_LABELS.condition_status
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionStatusOptionsForSelect.find(
                                        (status) => status.value === field.value
                                    ) || null
                                }
                                options={conditionStatusOptionsForSelect}
                                hasError={!!errors.condition_status}
                                errorText={errors.condition_status?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_status_additional_details")}
                        label={
                            CONSULTATION_NOTE_MED_LABELS.condition_status_additional_details
                        }
                        errorText={
                            errors.condition_status_additional_details?.message
                        }
                        hasError={!!errors.condition_status_additional_details}
                        isOptional
                    />
                </div>

                <MedNoteEPrescribe noteDetails={noteDetails} />
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>

                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
